import React, { useContext, useEffect, useState } from "react";
import { useAuthContext } from "../../../contexts/AuthContext";
import { Row } from "react-bootstrap";
import Sidebar from "../../UI/Sidebar";
import { getContent } from "../../SF";
import Loader from "../../UI/Loader";
import Filter from "../../UI/Filter";
import { getStorageValue } from "../../Utils";
import "../shared-styles.css";

const isDevelopment = process.env.NODE_ENV === "development";
const API_ENDPOINT = process.env.REACT_APP_API_BASE_URL + "/content";

const Content = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userOptionsOwners, setUserOptionsOwners] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState(null);
  const context = useAuthContext();

  const clearLocalStorage = () => {
    try {
      localStorage.clear();
      if (!isDevelopment && window.errorReporting) {
        window.errorReporting.captureEvent({
          message: "localStorage cleared in Content view",
          level: "info",
          extra: { view: "Content" },
        });
      }
    } catch (error) {
      if (!isDevelopment && window.errorReporting) {
        window.errorReporting.captureException(error);
      }
    }
  };

  useEffect(() => {
    if (!context?.ready) return;
    setLoading(true);
    getContent(context)
      .then((result) => {
        const owners = result.Owners.map((owner) => ({
          value: owner.Id,
          label: owner.Name,
        }));
        setUserOptionsOwners(owners);
        setData(result);
        setFilteredData(result);
      })
      .catch((error) => {
        if (!isDevelopment && window.errorReporting) {
          window.errorReporting.captureException(error);
        }
      })
      .finally(() => setLoading(false));
  }, [context]);

  // Search functionality
  useEffect(() => {
    if (!data || !data.Accounts) {
      setFilteredData(data);
      return;
    }

    if (!searchTerm.trim()) {
      setFilteredData(data);
      return;
    }

    const filtered = {
      ...data,
      Accounts: data.Accounts.filter((account) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          account.Name?.toLowerCase().includes(searchLower) ||
          account.ContentSpecialist?.toLowerCase().includes(searchLower) ||
          account.ActiveProducts?.some((product) =>
            product.Product2?.Name?.toLowerCase().includes(searchLower)
          ) ||
          account.AccountOwner?.toLowerCase().includes(searchLower) ||
          account.GargleCoordinator?.toLowerCase().includes(searchLower) ||
          account.VPSales?.toLowerCase().includes(searchLower) ||
          account.NextTask?.Subject?.toLowerCase().includes(searchLower) ||
          account.OverdueTasks?.some((task) =>
            task.Subject?.toLowerCase().includes(searchLower)
          )
        );
      }),
    };

    setFilteredData(filtered);
  }, [data, searchTerm]);

  const handleTaskUpdate = (task) => {
    if (!task) return;

    try {
      setData((prevData) => {
        if (!prevData) return prevData;

        // Create a new array of accounts with the updated task
        const updatedAccounts = prevData.Accounts.map((account) => {
          if (account.Id === task.AccountId) {
            return {
              ...account,
              AssignedContent: task.AssignedContent,
              AssignedTo: task.AssignedTo,
              ContentDueDate: task.DueDate,
            };
          }
          return account;
        });

        // Return the updated data structure
        return {
          ...prevData,
          Accounts: updatedAccounts,
        };
      });
    } catch (error) {
      if (!isDevelopment && window.errorReporting) {
        window.errorReporting.captureException(error);
      }
    }
  };

  if (!context?.ready) return <Loader />;
  if (loading || !data) return <Loader />;

  try {
    if (getStorageValue("gargle_owner_id"))
      context.OwnerId = getStorageValue("gargle_owner_id");
  } catch (err) {
    context.OwnerId = null;
    console.warn("[localStorage.clear] called in Content/index.js", {
      stack: new Error().stack,
    });
    window.localStorage.clear();
  }

  const columns = [
    "Account",
    "Content Specialist",
    "Content Record",
    "Overdue tasks",
    "Next Task",
    "Active Products",
    "Gargle Account Owner",
    "Gargle Coordinator",
    "VP Sales",
  ];

  if (data) {
    userOptionsOwners.push({ value: "null", label: "All Directors" });
    console.log("DATA " + data);

    for (const u of data.Owners) {
      userOptionsOwners.push({ value: u.Id, label: u.Name });
      console.log(userOptionsOwners);
    }

    setTimeout(() => {
      window["reset"]();

      window["buildDataTable"](
        [
          { width: "200px", targets: 1 },
          { width: "150px", targets: 2 },
          { width: "150px", targets: 3 },
          { width: "150px", targets: 4 },
          { width: "100px", targets: 5 },
          { width: "150px", targets: 6 },
          { width: "150px", targets: 7 },
        ],
        [0, "asc"]
      );
    }, 500);
  }

  if (context.OwnerId)
    context.Owner = userOptionsOwners.find(
      (item) => item.value === context.OwnerId
    );

  if (!context || !context.userInfo) return null;

  return (
    <>
      <Sidebar />
      <div className="content">
        <div className="views-container">
          <div className="views-modern-header">
            <div className="views-title-section">
              <div>
                <h1 className="views-page-title">Content</h1>
                <div className="views-subtitle">
                  {!context.OwnerId ||
                  !context.Owner ||
                  context.Owner.value === "null"
                    ? "All Directors"
                    : context.Owner.label}{" "}
                  - {filteredData?.Accounts?.length || 0} accounts
                </div>
              </div>
              <div className="views-filter-section">
                <select
                  className="views-filter-select"
                  value={context.OwnerId || ""}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    context.OwnerId = selectedId || null;
                    window.localStorage.setItem(
                      "gargle_owner_id",
                      selectedId || ""
                    );
                    window.location.reload();
                  }}
                >
                  <option value="">All Directors</option>
                  {userOptionsOwners.map((owner) => (
                    <option key={owner.value} value={owner.value}>
                      {owner.label}
                    </option>
                  ))}
                </select>
                <button
                  className="views-refresh-btn"
                  onClick={() => window.location.reload()}
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>

          <div className="views-table-container">
            <div className="views-search-container">
              <input
                type="text"
                className="views-search-input"
                placeholder="Search accounts, specialists, products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="views-table-wrapper">
              <table className="views-modern-table">
                <thead>
                  <tr>
                    {columns.map((c) => {
                      return <th key={"col-" + c}>{c}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filteredData?.Accounts?.map((a) => {
                    return (
                      <tr key={"row-" + a.Name}>
                        <td>
                          <a
                            className="views-table-link"
                            href={
                              "https://gargleinc.lightning.force.com/" + a.Id
                            }
                            target={"_blank"}
                            rel="noreferrer"
                          >
                            {a.Name}
                          </a>
                        </td>
                        <td>
                          {a.ContentSpecialist && (
                            <>
                              <p>{a.ContentSpecialist}</p>
                            </>
                          )}
                        </td>
                        <td>
                          {a.RelevantRecord && (
                            <>
                              <a
                                className="views-table-link"
                                href={
                                  "https://gargleinc.lightning.force.com/" +
                                  a.RelevantRecord.Id
                                }
                                target={"_blank"}
                                rel="noreferrer"
                              >
                                Open
                              </a>
                            </>
                          )}
                        </td>
                        <td>
                          {a.OverdueTasks &&
                            a.OverdueTasks.map((t) => {
                              return (
                                <p key={t.Id}>
                                  <a
                                    className="views-table-link"
                                    href={
                                      "https://gargleinc.lightning.force.com/" +
                                      t.Id
                                    }
                                    target={"_blank"}
                                    rel="noreferrer"
                                  >
                                    {t.Subject}
                                    <br />
                                    {t.ActivityDate}
                                  </a>
                                  <br />
                                  <br />
                                </p>
                              );
                            })}
                        </td>
                        <td>
                          {a.NextTask && (
                            <>
                              <a
                                className="views-table-link"
                                href={
                                  "https://gargleinc.lightning.force.com/" +
                                  a.NextTask.Id
                                }
                                target={"_blank"}
                                rel="noreferrer"
                              >
                                {a.NextTask.Subject}
                                <br />
                                {a.NextTask.ActivityDate}
                              </a>
                            </>
                          )}
                        </td>
                        <td>
                          {a.ActiveProducts &&
                            a.ActiveProducts.map((t) => {
                              return (
                                t.Product2 && (
                                  <p key={t.Id}>
                                    {t.Product2.Name} - $
                                    {t.Calculated_Amount_Actual__c}
                                  </p>
                                )
                              );
                            })}
                        </td>
                        <td>
                          {a.AccountOwner && (
                            <>
                              <p>{a.AccountOwner}</p>
                            </>
                          )}
                        </td>
                        <td>
                          {a.GargleCoordinator && (
                            <>
                              <p>{a.GargleCoordinator}</p>
                            </>
                          )}
                        </td>
                        <td>
                          {a.VPSales && (
                            <>
                              <p>{a.VPSales}</p>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Content;
